import React, { useEffect, useRef, useState } from "react";
import {
  Clock3,
  Flame,
  SendHorizontal,
  Sparkles,
  XCircle,
  Zap,
} from "lucide-react";
import RoomCard from "./RoomCard";


export default function RoomAnswerInput({
  disabled,
  onSubmitAnswer,
  debugAnswer,
  correctStreak = 0,
  fastStreak = 0,
  wrongStreak = 0,
  phase,
  remaining,
  partialMatchedWords = [],
  className = "",
}) {
  const [value, setValue] = useState("");
  const [localFeedback, setLocalFeedback] = useState("");
  const inputRef = useRef(null);

  useEffect(() => {
    if (!disabled) {
      const t = setTimeout(() => inputRef.current?.focus(), 50);
      return () => clearTimeout(t);
    }
  }, [disabled]);

  const submitCurrentValue = () => {
    if (disabled) {
      setLocalFeedback("La réponse est verrouillée pour le moment.");
      return;
    }

    const trimmedValue = value.trim();
    if (!trimmedValue) {
      setLocalFeedback("Tape une réponse avant d'envoyer.");
      return;
    }

    setLocalFeedback("");
    const wasSubmitted = onSubmitAnswer?.(trimmedValue);
    if (wasSubmitted !== false) {
      setValue("");
    } else {
      setLocalFeedback("La réponse n'a pas pu partir. Verifie la connexion au jeu.");
    }
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    submitCurrentValue();
  };

  const timerTone =
    phase === "question" && remaining <= 5
      ? "text-rose-600"
      : phase === "break"
      ? "text-amber-600"
      : "text-slate-900";
  const streakLevel = Math.min(correctStreak, 5);
  const streakTone =
    streakLevel >= 5
      ? "from-rose-500 via-orange-400 to-amber-300 text-white"
      : streakLevel >= 3
      ? "from-orange-400 via-amber-300 to-yellow-200 text-slate-950"
      : streakLevel >= 1
      ? "from-emerald-400 via-cyan-300 to-sky-200 text-slate-950"
      : "from-slate-100 to-white text-slate-500";

  return (
    <RoomCard contentClassName="space-y-4 !pt-2" className={className}>
      <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr),230px]">
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="rounded-[28px] border border-slate-200 bg-slate-50 p-4 shadow-inner">
            <div className="flex flex-wrap items-center gap-2 text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
              <span>Ta réponse</span>
              {debugAnswer && (
                <span className="rounded-full bg-emerald-50 px-2 py-1 text-[11px] font-semibold normal-case tracking-normal text-emerald-700">
                  DEV : {debugAnswer}
                </span>
              )}
              {correctStreak > 0 && (
                <span
                  className={`inline-flex items-center gap-1 rounded-full bg-gradient-to-r px-2 py-1 text-[11px] shadow-sm transition ${streakTone} ${
                    correctStreak >= 3 ? "animate-pulse" : ""
                  }`}
                >
                  <Flame className="h-3.5 w-3.5" />
                  Serie x{correctStreak}
                </span>
              )}
              {fastStreak > 0 && (
                <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-1 text-[11px] text-amber-700 shadow-sm">
                  <Zap className="h-3.5 w-3.5" />
                  Rapide x{fastStreak}
                </span>
              )}
              {wrongStreak > 0 && (
                <span className="inline-flex items-center gap-1 rounded-full bg-rose-100 px-2 py-1 text-[11px] text-rose-700 shadow-sm">
                  <XCircle className="h-3.5 w-3.5" />
                  A corriger x{wrongStreak}
                </span>
              )}
            </div>

            <div className="mt-3 flex flex-col gap-3 sm:flex-row">
              <input
                ref={inputRef}
                type="text"
                value={value}
                onChange={(event) => setValue(event.target.value)}
                placeholder={
                  disabled
                    ? "La réponse est verrouillée pour le moment"
                    : "Tape ta proposition ici"
                }
                disabled={disabled}
                className={`min-h-[56px] flex-1 rounded-2xl border px-4 text-slate-900 shadow-sm outline-none transition focus:border-orange-400 focus:ring-4 focus:ring-orange-100 ${
                  disabled
                    ? "cursor-not-allowed border-slate-200 bg-slate-100 text-slate-400"
                    : "border-slate-200 bg-white"
                }`}
              />

              <button
                type="button"
                onClick={submitCurrentValue}
                disabled={disabled}
                className="inline-flex min-h-[56px] items-center justify-center gap-2 rounded-2xl bg-slate-900 px-5 font-semibold text-white transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:bg-slate-300"
              >
                <SendHorizontal className="h-4 w-4" />
                Envoyer
              </button>
            </div>

            {localFeedback && (
              <div className="mt-4 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm font-semibold text-amber-700">
                {localFeedback}
              </div>
            )}

            {partialMatchedWords.length > 0 && (
              <div className="mt-4 rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm">
                <div className="flex flex-wrap items-center gap-2">
                  {partialMatchedWords.map((word) => (
                    <span
                      key={word}
                      className="rounded-lg bg-emerald-500 px-2 py-0.5 font-semibold text-white"
                    >
                      ✓ {word}
                    </span>
                  ))}
                  <span className="font-semibold text-emerald-700">
                    Il manque quelque chose…
                  </span>
                </div>
              </div>
            )}

            {correctStreak > 0 && (
              <div
                className={`mt-4 overflow-hidden rounded-2xl border border-white/70 bg-gradient-to-r p-3 shadow-sm transition ${streakTone}`}
              >
                <div className="flex items-center justify-between gap-3">
                  <div className="flex min-w-0 items-center gap-2">
                    <Sparkles className="h-4 w-4 flex-shrink-0" />
                    <div className="min-w-0">
                      <div className="text-sm font-semibold">
                        {correctStreak >= 5
                          ? "Serie en feu"
                          : correctStreak >= 3
                          ? "Belle serie"
                          : "Bonne réponse"}
                      </div>
                      <div className="mt-0.5 text-xs opacity-80">
                        La couleur monte tant que les bonnes réponses s'enchainent.
                      </div>
                    </div>
                  </div>
                  <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-2xl bg-white/40 text-lg font-black">
                    {correctStreak}
                  </div>
                </div>
              </div>
            )}
          </div>
        </form>

        <div className="rounded-[28px] border border-slate-200 bg-[linear-gradient(160deg,#fff7ed,#ffffff)] p-4">
          <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
            <Clock3 className="h-4 w-4" />
            Chrono
          </div>

          <div className={`mt-5 text-5xl font-semibold tracking-tight ${timerTone}`}>
            {remaining}s
          </div>

        </div>
      </div>

    </RoomCard>
  );
}
